import { DecimalPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { ButtonStackComponent } from "@components/button-stack/button-stack.component";
import { InputComponent } from "@components/input/input.component";
import { MapPopupContentComponent } from "@components/map/components/widgets/map-popup/map-popup-content/map-popup-content.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subject } from "rxjs";
import { IMapComponent } from "~app/common/components/map/map.models";
import { IMapDrawLayer, IMapGeometry, IMapLayer } from "~app/common/components/map/map.settings";
import { MapCalculationService } from "~app/common/components/map/services/map-calculation.service";
import { Subtyp1, Subtyp2 } from "~app/common/components/map/symbols/kartgeometri.constants";
import { KartgeometriKategori } from "~app/common/models/constants";
import { EventBridgeService } from "~app/common/services/event-bridge.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { MapEvents } from "~app/features/planera/events/map-events";
import { PlaneraKartaEventService } from "~app/features/planera/planera-karta/planera-karta-event.service";
import { KartaService } from "~app/features/planera/services/karta.service";
import { VirkeskopKartaEventService } from "~app/features/virkeskop/virkeskop-karta/virkeskop-karta-event.service";
import { IKartgeometri, IKartgeometriBasdataResponse } from "./kartgeometri.model";
import { PopupDialogInputMatrix } from "./popup-dialog-input-matrix";

export interface IEventData {
    atgardId?: number;
    geoid: any;
    geometri: IKartgeometri;
    isLeveransvirke?: boolean;
    layer: IMapLayer;
    param: any;
}

export interface IKartgeometriData {
    canEdit: boolean;
    event$: BehaviorSubject<IKartgeometriEvent>;
    eventService?: EventBridgeService;
    geometri: IKartgeometri;
    isLeveransvirke: boolean;
    isNew?: boolean;
    kategori: string;
    layer: IMapDrawLayer;
    mapGeometry: IMapGeometry;
    param: any;
    removeGraphicCB?: (payload: any, cb: () => void) => any;
}

export interface IKartgeometriEvent {
    action: string;
    payload: IEventData;
}

@Component({
    selector: "rask-kartgeometri-popup",
    templateUrl: "./kartgeometri-popup.component.html",
    styleUrls: ["./kartgeometri-popup.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MapPopupContentComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        ButtonStackComponent,
        ActionButtonComponent,
        SelectorComponent,
        RadiobuttonGroupComponent,
        TextareaComponent,
        DecimalPipe,
        TranslatePipe
    ]
})
export class KartgeometriPopupComponent implements OnInit {
    canEdit: boolean;
    event$: Subject<IKartgeometriEvent>;
    eventService: EventBridgeService;
    figurAreal: number;
    form: UntypedFormGroup;
    geometry: IMapGeometry;
    isLeveransvirke: boolean;
    isVirkeskop = false;
    kartgeometri: IKartgeometri;
    kartgeometriBasdata: IKartgeometriBasdataResponse;
    kategori = KartgeometriKategori.Arbetsorder;
    layer: IMapDrawLayer;
    map: IMapComponent;
    param: any;
    plockhuggningButtons = [
        {
            key: "Plockhuggning",
            value: "Ja"
        },
        {
            key: null,
            value: "Nej"
        }
    ];
    ready = false;
    stateMatrix: PopupDialogInputMatrix;
    subtypes: string[];
    subtypes2: string[];

    @Input() onClose: () => void;
    removeGraphicCB: (payload: any, cb: () => void) => any;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly formBinder = inject(FormBinderService);
    private readonly kartaService = inject(KartaService);
    private readonly mapCalculationService = inject(MapCalculationService);
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);
    private readonly translate = inject(TranslateService);
    private readonly virkeskopKartaEventService = inject(VirkeskopKartaEventService);

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("data")
    set data(value: IKartgeometriData) {
        this.kartgeometri = value.geometri;
        this.geometry = value.mapGeometry;
        this.kategori = value.kategori;
        this.eventService = value.eventService;
        this.event$ = value.event$;
        this.layer = value.layer;
        this.canEdit = value.canEdit;
        this.isLeveransvirke = value.isLeveransvirke;
        this.param = value.param;
        this.removeGraphicCB = value.removeGraphicCB;
    }

    get figurArealUnit() { return this.figurAreal ? "ha" : "Beräknar"; }

    emitIfValid() {
        if (!this.canEdit) {
            return;
        }
        requestAnimationFrame(() => {
            if (this.form.invalid) {
                return;
            }
            const eventData = this.toEventData(this.kartgeometri);
            this.eventService?.emit(MapEvents.geometriUpdated, eventData);
            this.planeraKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.event$?.next({
                action: MapEvents.geometriUpdated,
                payload: eventData
            });
        });
    }

    updateDialog() {
        const filtered = this.kartgeometriBasdata.subtyper3?.filter(s => s.namn === this.kartgeometri.subtyp2);
        this.subtypes = filtered && filtered.length > 0 ? filtered[0].typer : null;
        this.stateMatrix.update();
        this.subtypes2 = this.stateMatrix.limitSubtyp2 ? this.stateMatrix.limitedSubtyp2 : this.kartgeometriBasdata.subtyper2;
        this.tryUpdateOptions(this.subtypes2, this.kartgeometri.subtyp2);
        if (!this.canEdit && !this.form.disabled) {
            this.form.disable();
            return;
        }
        this.updateModel();
        this.formBinder.updateFromModel(this.form, this.kartgeometri);
    }

    onFormChanges = () => {
        this.updateDialog();
        this.emitIfValid();
    };

    readonly removeCurrentGraphic = () => {
        const eventData = this.toEventData(this.kartgeometri);
        this.eventService?.emit(MapEvents.geometriRemoved, eventData);
        this.event$?.next({
            action: MapEvents.geometriRemoved,
            payload: eventData
        });
        this.onClose();
    };

    abort = () => {
        if (this.kartgeometri.persistedKartgeometri.isNew) {
            this.removeCurrentGraphic();
        } else {
            if (this.isChanged(this.kartgeometri)) {
                ObjectUtils.resetObjectFromObject(this.kartgeometri, this.kartgeometri.persistedKartgeometri);
                this.onFormChanges();
            }
            this.onClose();
        }
    };

    addArea = () => {
        this.onClose();

        this.map.getSketch().add(this.layer, this.geometry, (g) => {
            this.kartgeometri.geometri = g.wkt;
            const eventData = this.toEventData(this.kartgeometri);
            this.virkeskopKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.planeraKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.event$?.next({
                action: MapEvents.geometriUpdated,
                payload: eventData
            });
        });
    };

    readonly askRemoveCurrentGraphic = () => {
        if (this.isLeveransvirke && this.kartgeometri.subtyp1 === Subtyp1.drivningspunkt && this.kartgeometri.subtyp2 === Subtyp2.ovrigOverfartLarmpunkt) {
            if (!ObjectUtils.isNullOrUndefined(this.removeGraphicCB)) {
                const result = this.removeGraphicCB(this.param, this.removeCurrentGraphic);
                if (typeof result === "boolean" && result) {
                    this.removeCurrentGraphic();
                }
            } else {
                this.removeCurrentGraphic();
            }
        } else {
            this.removeCurrentGraphic();
        }
    };

    canSave() {
        return true;
    }

    clearKarttext(e) {
        if (e !== Subtyp2.ovrigOverfartDrivningstext) {
            this.kartgeometri.karttext = null;
        }
    }

    cutArea = () => {
        this.onClose();
        this.map.getSketch().cut(this.layer, this.geometry, (g) => {
            this.kartgeometri.geometri = g.wkt;
            const eventData = this.toEventData(this.kartgeometri);
            this.virkeskopKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.planeraKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.event$?.next({
                action: MapEvents.geometriUpdated,
                payload: eventData
            });
        });
    };

    edit = () => {
        this.kartgeometri.isEditing = true;
        this.onClose();
        this.map.getSketch().edit(this.layer, this.geometry, (g) => {
            this.kartgeometri.isEditing = false;
            this.kartgeometri.geometri = g.wkt;
            const eventData = this.toEventData(this.kartgeometri);
            this.virkeskopKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.planeraKartaEventService.emit(MapEvents.geometriUpdated, eventData);
            this.event$?.next({
                action: MapEvents.geometriUpdated,
                payload: eventData
            });
        });
        this.map.getSketch().onAbort(() => {
            this.abort();
        });
    };

    hasPersistedData(): boolean {
        return !!this.kartgeometri.persistedKartgeometri;
    }

    init() {
        this.setupForm();
    }

    ngOnInit(): void {
        this.init();
        this.kartaService.getKartgeometriBasdata(this.kartgeometri.subtyp1, this.kategori, this.isLeveransvirke).subscribe(r => {
            this.kartgeometriBasdata = r;
            this.ready = true;
            this.stateMatrix = new PopupDialogInputMatrix(this.kartgeometri, this.kartgeometriBasdata);
            this.updateDialog();
            this.changeDetectorRef.detectChanges();
        });
    }

    setupForm() {
        if (!this.kartgeometri.persistedKartgeometri) {
            this.kartgeometri.persistedKartgeometri = ObjectUtils.deepCopy(this.kartgeometri);
        }
        this.form = this.formBinder.CreateGroup(this.kartgeometri);
        this.form.valueChanges.subscribe(this.onFormChanges);
    }

    setupOptions() {
        this.plockhuggningButtons =
            [
                {
                    key: this.stateMatrix.plockhuggning,
                    value: this.translate.instant("DEFAULT_LABEL_JA")
                },
                {
                    key: "",
                    value: this.translate.instant("DEFAULT_LABEL_NEJ")
                }
            ];
    }

    updateModel() {
        const typ = this.kartgeometri.subtyp1;
        if (!this.stateMatrix.showSubtyp3Selector) {
            this.kartgeometri.subtyp3 = null;
        }
        if (!this.kartgeometri.langd && (typ === this.stateMatrix.drivningslinje || typ === this.stateMatrix.hansynLinje || typ === this.stateMatrix.vag)) {
            this.mapCalculationService.getLength(this.kartgeometri.geometri).subscribe(r => this.kartgeometri.langd = r);
        }

        const isYta = [
            this.stateMatrix.hansynYta,
            this.stateMatrix.anvisningYta
        ].includes(typ);

        if (!this.figurAreal && isYta) {
            this.mapCalculationService.getArea(this.kartgeometri.geometri).subscribe(r => {
                this.figurAreal = this.mapCalculationService.calculateHektarFromArea(r);
            });
        }
    }

    private isChanged(kartGeometri: IKartgeometri): boolean {
        return ObjectUtils.isNullOrUndefined(kartGeometri.persistedKartgeometri) ||
            this.kartaService.isDifferent(kartGeometri, kartGeometri.persistedKartgeometri);
    }

    private toEventData(kartgeometri: IKartgeometri): IEventData {
        return {
            geometri: kartgeometri,
            isLeveransvirke: this.isLeveransvirke,
            atgardId: this.isLeveransvirke ? this.param : null,
            layer: this.layer,
            geoid: this.geometry.geoid,
            param: this.param
        };
    }

    private tryUpdateOptions<T>(options: T[], selectedOption: T): void {
        if (ObjectUtils.hasValue(selectedOption) && options.indexOf(selectedOption) === -1) {
            options.push(selectedOption);
        }
    }
}
